// Resolves which image backend the Unsplash-opted-in surfaces call through
// (`/app/v1/itineraries/getImage`). Follows the same backend-driven app-config
// pattern as `ENABLE_ACTIVITIES` (see `APP_CONFIG.IMAGE_SOURCE` in
// `src/constant/common.ts`). Backend adds an `IMAGE_SOURCE` row with
// `value: "Unsplash"` to flip on; absence / any other value = Google.
// Backend still falls back to Google automatically if Unsplash errors or hits
// the rate limit — this flag only controls what we *request*.
import { iAppConfig } from "../types";
import { APP_CONFIG } from "./common";

export function getImageSource(
  appConfig: iAppConfig[] | undefined
): "Google" | "Unsplash" {
  return appConfig?.find((c) => c.key === APP_CONFIG.IMAGE_SOURCE)?.value === "Unsplash"
    ? "Unsplash"
    : "Google";
}

// Slideshow photo counts. Backend allows 1..10 per call; each call is 1
// Unsplash search credit regardless of count, so bumping this up is free.
// Single-dest pulls the backend max on desktop; mobile is trimmed because
// decoding 10 × 1280×720 JPEGs competes with the day-accordion collapse
// animation on iOS Safari. Multi-dest gets 3 per city on both.
export const SLIDESHOW_IMAGES_SINGLE_DEST = 10;
export const SLIDESHOW_IMAGES_SINGLE_DEST_MOBILE = 4;
export const SLIDESHOW_IMAGES_MULTI_DEST = 3;

// What the chat DESTINATION handler requests per grid card. It only renders
// one image, but we pre-fetch the max so the itinerary-details slideshow can
// reuse from cache without hitting Unsplash again (same credit cost regardless
// of `count`).
export const CHAT_DESTINATION_IMAGE_COUNT = 10;

// --- Curated image overrides ---------------------------------------------
// Hardcoded photo overrides for specific destinations / themes per product
// (Kirill, 2026-06-07; Tokyo broadened to Japan-wide fallback later same day).
// Frontend-side override so existing saved itineraries (whose image URLs are
// stored in DB) and new fetches both honor the mapping. Backend override was
// reverted (revert ebd4fc9 on app-backend) so the slideshow can still pull
// live photos behind the curated hero.
// Match rules, in priority order (first match wins):
//   1. exact city name "kyoto" (case-insensitive, trimmed) → Kyoto URL
//   2. exact city name "osaka" (case-insensitive, trimmed) → Osaka URL
//   3. city in JAPAN_CITIES_FALLBACK  OR  country ∈ {JP, Japan}
//      OR  "cherry blossom" anywhere in title/city
//      → cherry-blossom-in-Japan URL
// The explicit JAPAN_CITIES_FALLBACK list catches saved itineraries where the
// `country` / `country_code` field is missing (e.g. a Tokyo upcoming trip
// whose stored destinationList entry has no country tag). Add a city here if
// you find a Japan-trip card still falling through to the placeholder.

const CHERRY_BLOSSOM_JAPAN_IMAGE =
  "https://images.unsplash.com/photo-1762018824220-d4a9ebda2b28?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";
const KYOTO_IMAGE =
  "https://images.unsplash.com/photo-1637679105331-a0cea188b83e?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";
const OSAKA_IMAGE =
  "https://images.unsplash.com/photo-1680061337399-a175cebe51b1?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D";

export type ImageOverrideContext = {
  city?: string | null;
  country?: string | null; // ISO-2 (e.g. "JP") or full name (e.g. "Japan")
  title?: string | null; // trip / suggestion / activity title, when available
};

const isJapan = (country?: string | null): boolean => {
  const c = (country ?? "").trim().toLowerCase();
  return c === "jp" || c === "japan";
};

// Common Japan cities that should fall back to the curated Japan photo when
// the saved trip's destinationList entry has no country tag. Add more here as
// you encounter Japan trips slipping through to the placeholder.
const JAPAN_CITIES_FALLBACK = new Set<string>([
  "tokyo",
  "yokohama",
  "hiroshima",
  "nara",
  "nagoya",
  "sapporo",
  "kobe",
  "fukuoka",
  "kanazawa",
  "nikko",
  "okinawa",
  "takayama",
]);

const containsCherryBlossom = (s?: string | null): boolean =>
  (s ?? "").toLowerCase().includes("cherry blossom");

/**
 * Returns the hardcoded image URL for a context if one of the override rules
 * matches, otherwise `null`. Pure and synchronous.
 */
export const getImageOverride = (
  ctx: ImageOverrideContext
): string | null => {
  const city = (ctx.city ?? "").trim().toLowerCase();
  // 1-2: City-specific overrides take precedence over the country-level rule.
  if (city === "kyoto") return KYOTO_IMAGE;
  if (city === "osaka") return OSAKA_IMAGE;
  // 3: Japan-wide fallback — any Tokyo / Hiroshima / Nara / etc. trip lands
  // here. Country tag wins when present; the JAPAN_CITIES_FALLBACK set covers
  // saved trips whose destinationList entry has no country_code. The cherry
  // blossom title/city check stays for themed explore cards before
  // destinationList resolves.
  if (
    isJapan(ctx.country) ||
    JAPAN_CITIES_FALLBACK.has(city) ||
    containsCherryBlossom(ctx.title) ||
    containsCherryBlossom(ctx.city)
  ) {
    return CHERRY_BLOSSOM_JAPAN_IMAGE;
  }
  return null;
};

/**
 * If a hardcoded override matches the context, returns it; otherwise returns
 * `originalUrl` (or `undefined` if that is null/empty). Use at render sites
 * to substitute saved itinerary URLs without touching the underlying data.
 */
export const resolveImageWithOverride = (
  originalUrl: string | null | undefined,
  ctx: ImageOverrideContext
): string | undefined => {
  const override = getImageOverride(ctx);
  if (override) return override;
  return originalUrl ?? undefined;
};
